// ─── WebSocket Event Protocol ────────────────────────────────────────

export type WsEvent =
  | { type: "chunk"; content: string }
  | { type: "thinking"; content: string }
  | { type: "tool_call"; name: string; args: string }
  | { type: "tool_result"; name: string; output: string }
  | { type: "done"; full_response: string }
  | { type: "error"; message: string }
  | { type: "history"; session_id: string; messages: unknown[] };

export type WsClientMessage = {
  type: "message";
  content: string;
  threadId: string;
  images?: Array<{ data: string; mimeType: string }>;
};

export type WsState = "connecting" | "connected" | "disconnected" | "error";

export interface WsManager {
  send: (data: WsClientMessage) => void;
  close: () => void;
  getState: () => WsState;
}

interface WsManagerConfig {
  url: string;
  onEvent: (event: WsEvent) => void;
  onStateChange: (state: WsState) => void;
}

const MAX_BACKOFF_MS = 30_000;
const PING_INTERVAL_MS = 30_000;
const MAX_QUEUE_SIZE = 10;

export function createWsManager(config: WsManagerConfig): WsManager {
  let ws: WebSocket | null = null;
  let state: WsState = "connecting";
  let backoffMs = 1000;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectDisabled = false;
  const queue: WsClientMessage[] = [];

  function setState(next: WsState) {
    if (next === state) return;
    state = next;
    config.onStateChange(state);
  }

  function startPing() {
    stopPing();
    pingTimer = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ping" }));
      }
    }, PING_INTERVAL_MS);
  }

  function stopPing() {
    if (pingTimer !== null) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
  }

  function flushQueue() {
    while (queue.length > 0 && ws?.readyState === WebSocket.OPEN) {
      const msg = queue.shift()!;
      ws.send(JSON.stringify(msg));
    }
  }

  function enqueue(data: WsClientMessage) {
    queue.push(data);
    if (queue.length > MAX_QUEUE_SIZE) {
      queue.shift();
    }
  }

  function scheduleReconnect() {
    if (reconnectDisabled || reconnectTimer !== null) return;
    // Don't revive the socket while the tab is hidden. The idle-pod reaper
    // (infra-doctrine §6.8) scales a pod to 0 when idle; if we reconnected
    // here, ws-auth would immediately wake it again, so a backgrounded tab
    // would bounce (reap → auto-wake) and never actually save cost. Staying
    // disconnected lets a hidden tab's pod reap and STAY asleep; onVisibilityChange
    // reconnects (and ws-auth wakes the pod) the moment the user returns.
    // Unintentional drops while hidden (network/pod restart) are likewise healed
    // on return — assistant replies persist to Convex regardless of the socket.
    if (typeof document !== "undefined" && document.hidden) return;
    const jitter = backoffMs * (1 + Math.random() * 0.5);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, jitter);
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
  }

  function connect() {
    if (reconnectDisabled) return;

    // Clean up previous socket to prevent orphaned handlers from corrupting state
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
      ws = null;
    }

    setState("connecting");

    ws = new WebSocket(config.url);

    ws.onopen = () => {
      setState("connected");
      backoffMs = 1000;
      startPing();
      flushQueue();
    };

    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(String(ev.data)) as WsEvent;
        if ((event as { type: string }).type === "pong") return;
        config.onEvent(event);
      } catch {
        // Ignore unparseable messages
      }
    };

    ws.onclose = () => {
      stopPing();
      setState("disconnected");
      scheduleReconnect();
    };

    ws.onerror = () => {
      setState("error");
      // Safety net: onerror fires before onclose per spec, but if onclose
      // never arrives the state would be stuck at "error" with no recovery.
      scheduleReconnect();
    };
  }

  // FMEA C3: Reconnect immediately when tab returns from background
  function onVisibilityChange() {
    if (
      !document.hidden &&
      !reconnectDisabled &&
      (state === "disconnected" || state === "error")
    ) {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      connect();
    }
  }

  document.addEventListener("visibilitychange", onVisibilityChange);

  // Start initial connection
  connect();

  return {
    send(data: WsClientMessage) {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(data));
      } else {
        enqueue(data);
      }
    },

    close() {
      reconnectDisabled = true;
      stopPing();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
        ws = null;
      }
      setState("disconnected");
    },

    getState() {
      return state;
    },
  };
}
